import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Globe, Plus, Trash2, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";

export default function AllowedDomains() {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/domains").then((r) => setRows(r.data)).catch((e) => toast.error(e?.response?.data?.detail || "Error"));
  useEffect(() => { load(); }, []);

  const add = async () => {
    const d = draft.trim().toLowerCase().replace(/^@/, "");
    if (!d) return;
    setLoading(true);
    try {
      await api.post("/domains", { domain: d });
      toast.success(`@${d} added`);
      setDraft("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove @${row.domain}? Existing users from this domain won't be deleted.`)) return;
    try {
      await api.delete(`/domains/${row.domain_id}`);
      toast.success("Domain removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1100px]" data-testid="allowed-domains-page">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-mono">Admin · Access control</div>
        <h1 className="font-heading text-2xl md:text-4xl font-bold tracking-tight">Allowed login domains</h1>
        <p className="text-zinc-500 mt-3 max-w-2xl">Users signing in with Google are only allowed in if their email domain is listed here. New users from non-primary domains land in a <span className="font-mono">PENDING</span> state awaiting CEO/ADMIN approval.</p>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-5 mb-6">
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <label className="text-xs text-zinc-500 uppercase tracking-widest font-mono">Add a domain</label>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-zinc-400 font-mono">@</span>
              <Input
                data-testid="domain-input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="partner-company.com"
                onKeyDown={(e) => e.key === "Enter" && add()}
              />
            </div>
          </div>
          <button onClick={add} disabled={loading || !draft.trim()} data-testid="domain-add-btn" className="inline-flex items-center gap-2 px-5 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50">
            <Plus size={14} /> Add domain
          </button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg md:overflow-x-auto md:overflow-hidden">
        <div className="hidden md:grid md:grid-cols-12 md:min-w-[760px] px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-zinc-200 font-mono">
          <div className="col-span-6">Domain</div>
          <div className="col-span-4">Added</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {rows.map((r) => (
          <div key={r.domain_id} className="flex flex-col gap-2 md:grid md:grid-cols-12 md:items-center md:min-w-[760px] md:gap-0 px-5 py-4 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50" data-testid={`domain-row-${r.domain}`}>
            <div className="col-span-6 flex items-center gap-3">
              <Globe size={16} className="text-zinc-400" />
              <span className="font-mono text-sm">@{r.domain}</span>
              {r.is_primary && <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-zinc-950 text-white rounded font-mono"><ShieldCheck size={10} /> PRIMARY</span>}
            </div>
            <div className="col-span-4 text-xs text-zinc-500 font-mono">{r.created_at?.slice(0, 10)}</div>
            <div className="col-span-2 text-right">
              {!r.is_primary && (
                <button data-testid={`domain-remove-${r.domain}`} onClick={() => remove(r)} className="p-2 text-zinc-500 hover:text-red-600 rounded-md hover:bg-red-50">
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="px-5 py-16 text-sm text-zinc-500 text-center">No domains configured.</div>
        )}
      </div>
    </div>
  );
}
